import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    componentName?: string;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary
 * 
 * Captura erros de renderização em componentes filhos e exibe uma UI de fallback.
 * Útil para evitar que a tela inteira fique branca (White Screen of Death).
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error(`[ErrorBoundary] Erro capturado em ${this.props.componentName || 'Componente'}:`, error, errorInfo);
        this.setState({ errorInfo });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                    <div className="mb-4 flex justify-center">
                        <div className="rounded-full bg-red-100 p-3">
                            <AlertTriangle className="size-8 text-red-600" />
                        </div>
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-red-900">
                        Algo deu errado
                    </h3>
                    <p className="mb-4 text-sm text-red-700">
                        Não foi possível carregar {this.props.componentName ? `o componente ${this.props.componentName}` : 'este conteúdo'}.
                    </p>

                    {/* Detalhes técnicos (apenas se houver erro capturado) */}
                    {this.state.error && (
                        <div className="mb-4 overflow-hidden rounded bg-white p-3 text-left shadow-sm">
                            <p className="mb-1 text-xs font-bold text-red-800">Erro Técnico:</p>
                            <code className="block max-h-32 overflow-auto whitespace-pre-wrap text-xs text-red-600">
                                {this.state.error.toString()}
                            </code>
                        </div>
                    )}

                    <button
                        onClick={this.handleRetry}
                        className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        <RefreshCw className="size-4" />
                        Tentar Novamente
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}
